/**
 * SeasonBoard (MEMBERSHIPS-02 P3, 2026-09-08) — the Memberships home.
 *
 * Two seasons a year are the real unit of work, so the page opens on them:
 * "Fall 2026 · Furnace checks" with owed → invited → picked → booked → done,
 * the burn-down, the weekly pace to finish by the date the office sets, last
 * year's same season at this point, the at-risk count, and the queue under it
 * (filter by zone, plan, renewal-at-this-visit, at-risk, month; per-row Open ·
 * Book · Snooze · Decline). Built to the mock-up in the "Pegasus Memberships"
 * proposal Seth approved. Waves (invites) arrive with P4 — the button is here,
 * honest about it.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ChevronDown, Flame, Snowflake, RefreshCw, Send, Search, Phone, CalendarPlus, Clock, X, RotateCcw, ExternalLink, PauseCircle, Hourglass } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Tabs } from "@/components/ui/Tabs";
import { OverflowMenu } from "@/components/ui/Menu";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { displayCustomerName } from "@/lib/format";
import { onErrToast } from "@/components/ui/mutationToast";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { CreateDispatchModal } from "@/features/customer-360/CreateDispatchModal";
import { useActiveTechs } from "@/features/board/api";
import {
  seasonFromKey, currentSeasonKey, defaultFinish, useSeasonVisits, useSeasonPace, useSeasonFinish, useSetSeasonFinish,
  useVisitAction, useReconcileNow, useInviteWave, needsCall, summarize, pace, stillToDo, isOutstanding, STATUS_LABEL, MONTH_SHORT,
  type SeasonVisit, type VisitStatus, type WaveResult,
} from "./season";

/** DESIGN-CALM-01 Wave 2: the status is ONE word. Owed and the closed states
 *  are plain text; only a live state earns a soft color. */
const STATUS_TONE: Partial<Record<VisitStatus, BadgeTone>> = {
  invited: "info",
  picked: "info",
  booked: "good",
  completed: "good",
  snoozed: "warn",
  deferred: "warn",
  waiting: "warn",
  forfeited: "bad",
};
const STATUS_ORDER: Record<VisitStatus, number> = { open: 0, invited: 1, picked: 2, waiting: 3, deferred: 4, snoozed: 5, booked: 6, completed: 7, declined: 8, forfeited: 9, skipped: 10, cancelled: 11 };

type Quick = "all" | "outstanding" | "invited" | "picked" | "call" | "booked" | "done" | "at_risk" | "renewal" | "carried" | "ended" | "deferred" | "waiting" | "snoozed" | "declined";
/** The four that matter every day — a segmented control (DESIGN-CALM-01 Wave 2:
 *  four options or fewer = segments; the rest live under "More filters"). */
const SEGMENTS: { key: Quick; label: string }[] = [
  { key: "outstanding", label: "Still Owed" },
  { key: "booked", label: "Booked" },
  { key: "done", label: "Done" },
  { key: "all", label: "All" },
];
const MORE: { key: Quick; label: string; title?: string }[] = [
  { key: "call", label: "Call List", title: "No text or email on file, or invited and quiet for a week" },
  { key: "at_risk", label: "At Risk", title: "The plan's term ends before the check" },
  { key: "renewal", label: "Renewal Visits", title: "The last check of the term — sell the renewal there" },
  { key: "invited", label: "Invited" },
  { key: "picked", label: "Picked a Day" },
  { key: "carried", label: "Carried Over", title: "Owed from an earlier season" },
  { key: "ended", label: "Ended Plans", title: "The term ended; the check is still owed inside its window" },
  { key: "deferred", label: "Deferred" },
  { key: "waiting", label: "Waiting on the Customer" },
  { key: "snoozed", label: "Snoozed" },
  { key: "declined", label: "Declined" },
];
function matchesQuick(r: SeasonVisit, quick: Quick): boolean {
  switch (quick) {
    case "all": return true;
    case "outstanding": return isOutstanding(r.status);
    case "invited": return r.status === "invited";
    case "picked": return r.status === "picked";
    case "call": return needsCall(r);
    case "booked": return r.status === "booked";
    case "done": return r.status === "completed";
    case "at_risk": return !!r.at_risk;
    case "renewal": return !!(r.renewal_at_this_visit && stillToDo(r.status));
    case "snoozed": return r.status === "snoozed";
    case "deferred": return r.status === "deferred";
    case "waiting": return r.status === "waiting";
    case "carried": return !!(r.carried_from && isOutstanding(r.status));
    case "ended": return !!(r.term_ended && isOutstanding(r.status));
    case "declined": return r.status === "declined";
  }
}
const QUICK_LABEL: Record<Quick, string> = Object.fromEntries([...SEGMENTS, ...MORE].map((q) => [q.key, q.label])) as Record<Quick, string>;

const fmtDay = (iso: string | null) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "");
const fmtMd = (iso: string | null) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");
/** "May 21" this year, "May 21, 2027" another — a "use by" a year out needs its year. */
const fmtMdY = (iso: string | null) => { if (!iso) return ""; const d = new Date(iso + "T00:00:00"); return d.toLocaleDateString("en-US", d.getFullYear() === new Date().getFullYear() ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" }); };
const num = (n: number) => n.toLocaleString();

export function SeasonBoard() {
  const navigate = useNavigate();
  const canEdit = useCan("agreements.edit");
  const [key, setKey] = useState<number>(() => currentSeasonKey());
  const season = seasonFromKey(key);
  const { data: rows, isLoading } = useSeasonVisits(key);
  const { data: techs } = useActiveTechs("03");
  // The tech's first name for a placed row — React state, not a module variable,
  // so a row rendered before the techs arrive still gets the name (Wave 2 fix).
  const techNames = useMemo(() => Object.fromEntries((techs ?? []).map((t) => [t.empno, t.empname ?? `${t.firstname ?? ""} ${t.lastname ?? ""}`])) as Record<string, string>, [techs]);
  const { data: paceLast } = useSeasonPace(key);
  const { data: finishSaved } = useSeasonFinish(key);
  const setFinish = useSetSeasonFinish();
  const reconcile = useReconcileNow();
  const wave = useInviteWave();
  const finish = finishSaved ?? defaultFinish(season);
  const [waveOpen, setWaveOpen] = useState(false);
  const [waveSize, setWaveSize] = useState(150);
  const [waveResult, setWaveResult] = useState<WaveResult | null>(null);

  const [search, setSearch] = useState("");
  const [zone, setZone] = useState("all");
  const [term, setTerm] = useState("all");
  const [month, setMonth] = useState("all");
  const [quick, setQuick] = useState<Quick>("outstanding");
  const [limit, setLimit] = useState(150);

  const all = rows ?? [];
  const totals = useMemo(() => summarize(all), [all]);
  const remaining = all.filter((r) => stillToDo(r.status)).length;
  const p = pace(remaining, finish);
  const lastPct = paceLast && paceLast.last_total > 0 ? Math.round((paceLast.last_done_by_point / paceLast.last_total) * 100) : null;
  const donePct = totals.owed > 0 ? Math.round((totals.done / totals.owed) * 100) : 0;

  const zones = useMemo(() => Array.from(new Set(all.map((r) => r.city).filter(Boolean) as string[])).sort(), [all]);
  const months = useMemo(() => Array.from(new Set(all.map((r) => r.target_month))).sort((a, b) => a - b), [all]);

  const counts = useMemo(() => {
    const out = {} as Record<Quick, number>;
    for (const q of [...SEGMENTS, ...MORE]) out[q.key] = all.filter((r) => matchesQuick(r, q.key)).length;
    return out;
  }, [all]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter((r) => {
        if (zone !== "all" && r.city !== zone) return false;
        if (term !== "all" && String(r.term_years ?? "") !== term) return false;
        if (month !== "all" && String(r.target_month) !== month) return false;
        if (!matchesQuick(r, quick)) return false;
        if (q && !((r.customer_fullname ?? "").toLowerCase().includes(q) || r.custno.includes(q) || (r.address ?? "").toLowerCase().includes(q))) return false;
        return true;
      })
      .sort((a, b) =>
        Number(b.at_risk) - Number(a.at_risk)
        || STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
        || a.target_date.localeCompare(b.target_date)
        || (a.customer_fullname ?? "").localeCompare(b.customer_fullname ?? ""));
  }, [all, search, zone, term, month, quick]);
  const shown = filtered.slice(0, limit);

  const SeasonIcon = season.kind === "fall" ? Flame : Snowflake;

  return (
    <div className="space-y-4">
      {/* ------------------------------------------------------------ the season */}
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 bg-gradient-to-r from-brand-50/70 via-transparent to-transparent px-5 py-3">
          <button type="button" onClick={() => setKey((k) => k - 1)} title="Previous season" className="rounded-md p-1 text-slate-400 hover:bg-white hover:text-slate-700"><ChevronLeft className="h-4 w-4" /></button>
          <span className={cn("flex h-8 w-8 items-center justify-center rounded-lg", season.kind === "fall" ? "bg-amber-50 text-amber-600" : "bg-sky-50 text-sky-600")}><SeasonIcon className="h-4 w-4" /></span>
          <h2 className="text-lg font-bold tracking-tight text-slate-900 [font-family:var(--font-display)]">{season.label} · {season.checkLabel}</h2>
          <button type="button" onClick={() => setKey((k) => k + 1)} title="Next season" className="rounded-md p-1 text-slate-400 hover:bg-white hover:text-slate-700"><ChevronRight className="h-4 w-4" /></button>
          {key !== currentSeasonKey() && (
            <button type="button" onClick={() => setKey(currentSeasonKey())} className="text-xs font-medium text-brand-700 hover:text-brand-900">Back to this season</button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/agreements/planner")}
              title="The Tune-Up Planner — pick a light day and Pegasus fills it with the nearest still-owed checks"
              className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              <CalendarPlus className="h-3.5 w-3.5" /> Fill the week
            </button>
            {canEdit && (
              <button
                type="button"
                onClick={() => { setWaveOpen((v) => !v); setWaveResult(null); }}
                title="Invite the next members to pick their own day — texts first, email when there's no cell. Held until comms go live."
                className={cn("inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold shadow-e1 transition-colors", waveOpen ? "bg-brand-800 text-white" : "bg-brand-700 text-white hover:bg-brand-800")}
              >
                <Send className="h-3.5 w-3.5" /> Send next wave
              </button>
            )}
            <OverflowMenu
              label="More"
              items={[
                { label: reconcile.isPending ? "Reading ESC…" : "Refresh from ESC", icon: <RefreshCw className={cn(reconcile.isPending && "animate-spin")} />, disabled: reconcile.isPending, title: "Read ESC's tickets now (Pegasus does this every 15 minutes on its own)", onSelect: () => reconcile.mutate(undefined, { onError: onErrToast("Couldn't read the mirror") }) },
              ]}
            />
          </div>
        </div>

        {waveOpen && (
          <div className="flex flex-wrap items-center gap-3 border-b border-brand-100 bg-brand-50/50 px-5 py-3 text-sm">
            <span className="font-semibold text-slate-900">Invite the next</span>
            <input type="number" min={1} max={1000} value={waveSize} onChange={(e) => setWaveSize(Math.max(1, Math.min(1000, Number(e.target.value) || 0)))} className="h-8 w-20 rounded-md border border-hairline bg-white px-2 text-sm tabular-nums" />
            <span className="inline-flex items-center gap-1.5 text-slate-600">
              owed visits
              <InfoTip label="How a wave works">At-risk visits go first, then by usual month and zone. Each member gets a text with a pick-a-day link, or an email when there's no cell; anyone with neither goes on the call list. One nudge after 10 days.</InfoTip>
            </span>
            <button
              type="button"
              disabled={wave.isPending || totals.open === 0}
              onClick={() => wave.mutate({ seasonKey: key, limit: waveSize }, { onSuccess: (r) => setWaveResult(r), onError: onErrToast("The wave didn't go out") })}
              className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            >
              {wave.isPending ? "Sending…" : `Send ${Math.min(waveSize, totals.open)} invites`}
            </button>
            <button type="button" onClick={() => setWaveOpen(false)} className="text-xs font-medium text-slate-500 hover:text-slate-700">Close</button>
            {waveResult && (
              <span className="basis-full text-xs text-slate-700">
                Sent: <b>{waveResult.invited_sms}</b> texts · <b>{waveResult.invited_email}</b> emails · <b>{waveResult.no_line}</b> with no line (on the call list) · {num(waveResult.remaining)} still uninvited.
                <span className="text-slate-500"> Messages are held while comms are in testing — see Comms &amp; Automations.</span>
              </span>
            )}
          </div>
        )}

        <div className="px-5 py-4">
          {/* the numbers — zero counts don't render (Wave 1 rule); the bar under them is the legend */}
          <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
            <Big n={totals.owed} label="owed" />
            {totals.invited > 0 && <Big n={totals.invited} label="invited" tone="sky" />}
            {totals.picked > 0 && <Big n={totals.picked} label="picked a day" tone="sky" />}
            <Big n={totals.booked} label="booked" tone="brand" />
            <Big n={totals.done} label="done" tone="emerald" />
            <Big n={donePct} label="of the season" pct />
          </div>
          <div className="mt-2.5 flex h-2 w-full overflow-hidden rounded-full bg-slate-100" title={`${donePct}% done · ${num(totals.done)} done · ${num(totals.booked)} booked · ${num(totals.invited + totals.picked)} invited or picked · ${num(remaining)} still to go`}>
            {totals.owed > 0 && (
              <>
                <div className="h-full bg-emerald-500" style={{ width: `${(totals.done / totals.owed) * 100}%` }} />
                <div className="h-full bg-brand-500" style={{ width: `${(totals.booked / totals.owed) * 100}%` }} />
                <div className="h-full bg-sky-300" style={{ width: `${((totals.invited + totals.picked) / totals.owed) * 100}%` }} />
              </>
            )}
          </div>

          {/* one quiet line: pace, last year, the two lists worth a click, the leftovers.
              The explanations live on hover (DESIGN-CALM-01: no sentences in the chrome). */}
          <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
            <span className="inline-flex items-center gap-1.5" title={`About ${p.techDaysPerDay} tech-day${p.techDaysPerDay === 1 ? "" : "s"} a day${p.weeksLeft > 0 ? ` for ${p.weeksLeft} week${p.weeksLeft === 1 ? "" : "s"}` : ""} — change the date to change the pace`}>
              <b className="tabular-nums text-slate-800">{num(p.perWeek)} a week</b> to finish by
              <input
                type="date"
                value={finish}
                onChange={(e) => e.target.value && setFinish.mutate({ seasonKey: key, date: e.target.value }, { onError: onErrToast("Couldn't save the finish date") })}
                className="h-6 rounded-md border border-hairline bg-white px-1.5 text-xs text-slate-700 focus:border-brand-500 focus:outline-none"
                aria-label="Finish the season by"
              />
            </span>
            {paceLast && lastPct !== null && (
              <span title={`${num(paceLast.last_done_by_point)} of ${num(paceLast.last_total)} done by ${fmtMd(paceLast.point_date)}`}>
                {paceLast.last_season.toLowerCase().replace(/^(fall|spring)/, "Last $1")} at this point: <b className="text-slate-800">{lastPct}%</b>
              </span>
            )}
            <button type="button" onClick={() => setQuick("at_risk")} title="The plan's term ends before the check — click for the list" className={cn("font-semibold underline decoration-dotted underline-offset-2", totals.atRisk > 0 ? "text-amber-800 hover:text-amber-900" : "text-slate-500")}>
              {num(totals.atRisk)} at risk
            </button>
            <button type="button" onClick={() => setQuick("renewal")} title="The last check of the term — sell the renewal there. Click for the list" className="font-semibold text-slate-700 underline decoration-dotted underline-offset-2 hover:text-slate-900">
              {num(totals.renewals)} renewal visits
            </button>
            {(totals.carried > 0 || totals.ended > 0) && (
              <button type="button" onClick={() => setQuick("carried")} className="text-slate-500 hover:text-slate-800" title="Checks owed from an earlier season, or from plans whose term has ended — still deliverable inside their window">
                {num(totals.carried)} carried over{totals.ended ? ` · ${num(totals.ended)} from ended plans` : ""}
              </button>
            )}
            {(totals.snoozed > 0 || totals.deferred > 0 || totals.waiting > 0 || totals.declined > 0 || totals.forfeited > 0) && (
              <span className="text-slate-400">
                {[totals.deferred ? `${num(totals.deferred)} deferred` : null, totals.waiting ? `${num(totals.waiting)} waiting` : null, totals.snoozed ? `${num(totals.snoozed)} snoozed` : null, totals.declined ? `${num(totals.declined)} declined` : null, totals.forfeited ? `${num(totals.forfeited)} forfeited` : null].filter(Boolean).join(" · ")}
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* ------------------------------------------------------------ filters
          Four segments with counts; the other eleven behind "More filters"
          (empty ones greyed inside the menu, never on the row). */}
      {(() => {
        const moreActive = MORE.some((m) => m.key === quick);
        return (
          <div className="flex flex-wrap items-center gap-2">
            <Tabs
              size="sm"
              items={SEGMENTS.map((sg) => ({ key: sg.key, label: sg.label, count: num(counts[sg.key] ?? 0) }))}
              value={moreActive ? "" : quick}
              onChange={(k) => setQuick(k as Quick)}
            />
            <OverflowMenu
              align="start"
              trigger={(open) => (
                <span className={cn("inline-flex h-8 items-center gap-1 rounded-lg border px-2.5 text-xs font-medium transition-colors", moreActive ? "border-brand-200 bg-brand-50 text-brand-800" : open ? "border-slate-300 bg-slate-50 text-slate-800" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")}>
                  {moreActive ? `${QUICK_LABEL[quick]} · ${num(counts[quick] ?? 0)}` : "More filters"} <ChevronDown className="h-3.5 w-3.5" />
                </span>
              )}
              items={[
                ...MORE.map((m) => ({ key: m.key, label: m.label, count: num(counts[m.key] ?? 0), muted: (counts[m.key] ?? 0) === 0, checked: quick === m.key, title: m.title, onSelect: () => setQuick(m.key) })),
                ...(moreActive ? ["divider" as const, { key: "clear", label: "Clear this filter", onSelect: () => setQuick("outstanding") }] : []),
              ]}
            />
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, address or account #" className="h-8 w-48 rounded-lg border border-hairline bg-white pl-8 pr-2 text-xs text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
            </div>
            <Sel value={zone} onChange={setZone} options={[["all", "Any zone"], ...zones.map((z) => [z, z] as [string, string])]} />
            <Sel value={term} onChange={setTerm} options={[["all", "Any plan"], ["1", "1-Year"], ["2", "2-Year"], ["3", "3-Year"], ["5", "5-Year"], ["10", "10-Year"]]} />
            <Sel value={month} onChange={setMonth} options={[["all", "Any month"], ...months.map((m) => [String(m), MONTH_SHORT[m]] as [string, string])]} />
            <span className="ml-auto text-xs text-slate-500"><b className="tabular-nums text-slate-800">{num(filtered.length)}</b> of {num(all.length)}</span>
          </div>
        );
      })()}

      {/* ------------------------------------------------------------ the queue */}
      {isLoading ? (
        <Card className="p-6 text-sm text-slate-400">Loading the season…</Card>
      ) : all.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500">Nothing owed in {season.label} yet — the queue fills as plans run into this season.</Card>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500">Nothing matches those filters.</Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,0.9fr)_minmax(0,1.2fr)_72px_minmax(0,1.9fr)_auto] gap-x-3 border-b border-slate-100 bg-slate-50/70 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <span>Member</span><span>Zone</span><span>Plan</span><span>Usual month</span><span>Status</span><span className="text-right">Actions</span>
          </div>
          <ul className="divide-y divide-slate-100">
            {shown.map((r) => (
              <VisitRow key={r.id} r={r} canEdit={canEdit} techNames={techNames} onOpen={() => navigate(`/customers/${r.custno}?tab=agreements&from=agreements`)} />
            ))}
          </ul>
          {filtered.length > shown.length && (
            <button type="button" onClick={() => setLimit((l) => l + 300)} className="w-full border-t border-slate-100 px-4 py-2.5 text-center text-xs font-medium text-brand-700 hover:bg-brand-50/40">
              Show more — {num(filtered.length - shown.length)} to go
            </button>
          )}
        </Card>
      )}
    </div>
  );
}

function Big({ n, label, tone, pct }: { n: number; label: string; tone?: "brand" | "emerald" | "sky"; pct?: boolean }) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className={cn("text-2xl font-bold tabular-nums tracking-tight [font-family:var(--font-display)]", tone === "emerald" ? "text-emerald-700" : tone === "brand" ? "text-brand-700" : tone === "sky" ? "text-sky-700" : "text-slate-900")}>{pct ? `${n}%` : num(n)}</span>
      <span className="text-xs font-medium text-slate-500">{label}</span>
    </span>
  );
}

function Sel({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="h-8 rounded-lg border border-hairline bg-white px-2 text-xs text-slate-700 focus:border-brand-500 focus:outline-none">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function statusDetail(r: SeasonVisit): string {
  switch (r.status) {
    case "open": return (r.reason ?? "").startsWith("no text or email") ? "no text or email on file — call" : r.esc_dispatch ? `ESC #${r.esc_dispatch} posted · not booked` : r.reason ?? "no ticket yet";
    case "invited": return `invited ${fmtMd(r.invited_at?.slice(0, 10) ?? null)}${r.nudged_at ? ` · nudged ${fmtMd(r.nudged_at.slice(0, 10))}` : ""}${needsCall(r) ? " · call them" : ""}`;
    case "picked": return `wants ${fmtDay(r.booked_date)} · ${(r.reason ?? "").replace(/^picked\s*/, "")} — book it`;
    case "booked": return `${fmtDay(r.booked_date) || "booked"}${r.esc_dispatch ? ` · ESC #${r.esc_dispatch}` : ""}`;
    case "completed": return `done ${fmtDay(r.completed_date)}${r.esc_dispatch ? ` · #${r.esc_dispatch}` : ""}`;
    case "snoozed": return `until ${fmtMd(r.snoozed_until)}${r.reason ? ` — ${r.reason}` : ""}`;
    case "deferred": return `${r.deferred_to ? `until ${fmtMd(r.deferred_to)} — ` : ""}${r.reason ?? "couldn't be done"}`;
    case "waiting": return r.reason ?? "tried them — no answer yet";
    case "declined": return r.reason ?? "declined";
    case "forfeited": return r.reason ?? "term ended";
    default: return r.reason ?? "";
  }
}

/** PLANNER-02: the tech's first name for a placed row. */
function placedTechName(empno: string | null, names: Record<string, string>): string {
  if (!empno) return "a tech";
  const n = names[empno];
  return n ? n.trim().split(/\s+/)[0] : empno;
}

function VisitRow({ r, canEdit, techNames, onOpen }: { r: SeasonVisit; canEdit: boolean; techNames: Record<string, string>; onOpen: () => void }) {
  const act = useVisitAction();
  const [mode, setMode] = useState<null | "snooze" | "decline" | "defer" | "book">(null);
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const name = displayCustomerName(null, r.customer_fullname);
  const live = stillToDo(r.status);
  const planShort = r.term_years ? `${r.term_years}-Yr PLUS` : (r.plan_name ?? "Plan");
  const phone = r.phone?.replace(/[^0-9+]/g, "") ?? "";
  const plusMonths = (n: number) => { const d = new Date(); d.setMonth(d.getMonth() + n); d.setDate(1); return d.toISOString().slice(0, 10); };

  // DESIGN-CALM-01 Wave 2 — the row: one line per cell, one colored word per
  // row, one visible action; the rest under "…". Rows went from 74 to ~44px.
  const closed = r.status === "snoozed" || r.status === "declined" || r.status === "deferred" || r.status === "waiting" || r.status === "skipped";
  const planFacts = [
    r.renewal_at_this_visit && live ? "renewal visit" : null,
    r.carried_from && live ? `carried from ${r.carried_from}` : null,
    r.term_ended && live && r.owed_until ? `use by ${fmtMdY(r.owed_until)}` : null,
  ].filter(Boolean).join(" · ");
  const tone = STATUS_TONE[r.status];
  const placed = r.placed_date && r.status !== "booked" && r.status !== "completed"
    ? `${r.placed_status === "proposed" ? "proposed" : "on"} ${placedTechName(r.placed_tech, techNames)} · ${fmtDay(r.placed_date)}`
    : null;
  const detail = statusDetail(r);
  const secondLine = placed ?? (r.status === "open" ? "" : detail);
  const menu = [
    ...(canEdit && isOutstanding(r.status)
      ? [
          { label: "Snooze", icon: <Clock />, onSelect: () => { setMode("snooze"); setUntil(plusMonths(1)); } },
          ...(r.status !== "deferred" ? [{ label: "Defer", icon: <PauseCircle />, title: "Couldn't be done — say why (furnace needs a part, customer deciding)", onSelect: () => { setMode("defer"); setUntil(""); } }] : []),
          ...(r.status !== "waiting" ? [{ label: "No Answer", icon: <Hourglass />, title: "You've tried and heard nothing — its own list, its own nudge once texting is live", onSelect: () => act.mutate({ id: r.id, action: "waiting", reason: `tried ${fmtMd(new Date().toISOString().slice(0, 10))} — no answer yet` }, { onError: onErrToast("Couldn't mark it waiting") }) }] : []),
          { label: "Decline", icon: <X />, danger: true, onSelect: () => setMode("decline") },
          "divider" as const,
        ]
      : []),
    { label: "Open the customer", icon: <ExternalLink />, onSelect: onOpen },
  ];

  return (
    <li className="px-4 py-2">
      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,0.8fr)_minmax(0,1.6fr)_56px_minmax(0,1.7fr)_auto] items-center gap-x-3">
        <div className="min-w-0">
          <button type="button" onClick={onOpen} className="block max-w-full truncate text-left text-[13px] font-semibold text-slate-900 hover:text-brand-700">{name}</button>
          <div className="truncate text-xs text-slate-500">{r.address ?? "—"}{r.systems && r.systems > 1 ? ` · ${r.systems} systems` : ""}{r.task_label.includes("system") ? ` · ${r.task_label.replace(/^.*\((system \d+)\).*$/, "$1")}` : ""}</div>
        </div>
        <div className="truncate text-xs text-slate-600">{r.city ?? "—"}</div>
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5 text-xs text-slate-800">
            <span className="truncate">{planShort}{r.term_end ? ` · ${r.term_ended ? "ended" : "ends"} ${fmtMd(r.term_end)}` : ""}</span>
            {r.at_risk && live ? (
              <Badge size="xs" tone="warn" title="The plan's term ends before the check">at risk</Badge>
            ) : r.term_ended && live ? (
              <Badge size="xs" tone="warn" title="The term has ended; the check is still owed inside its window — the renewal conversation is now">plan ended</Badge>
            ) : null}
          </div>
          {planFacts && <div className="truncate text-xs text-slate-500">{planFacts}</div>}
        </div>
        <div className="text-xs font-medium text-slate-700">{MONTH_SHORT[r.target_month]}</div>
        <div className="min-w-0">
          {tone ? (
            <Badge size="xs" tone={tone} title={detail || undefined}>{STATUS_LABEL[r.status]}</Badge>
          ) : (
            <span className={cn("text-xs font-medium", r.status === "open" ? "text-slate-800" : "text-slate-500")} title={detail || undefined}>{STATUS_LABEL[r.status]}</span>
          )}
          {secondLine && <div className="truncate text-xs text-slate-500" title={placed ? (r.placed_status === "proposed" ? "Pegasus proposed this tech-day — approve it in the planner" : "On a planner day — the call list for that tech-day") : detail}>{secondLine}</div>}
        </div>
        <div className="flex shrink-0 items-center justify-end gap-1.5">
          {phone && <a href={`tel:${phone}`} title={`Call ${r.phone}`} className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-hairline bg-white text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-700"><Phone className="h-3.5 w-3.5" /></a>}
          {canEdit && isOutstanding(r.status) ? (
            <button type="button" onClick={() => setMode(mode === "book" ? null : "book")} className="inline-flex h-7 items-center gap-1 rounded-md bg-brand-50 px-2.5 text-xs font-semibold text-brand-700 ring-1 ring-inset ring-brand-100 transition-colors hover:bg-brand-100"><CalendarPlus className="h-3.5 w-3.5" /> Book</button>
          ) : canEdit && closed ? (
            <button type="button" onClick={() => act.mutate({ id: r.id, action: "reopen" }, { onError: onErrToast("Couldn't bring it back") })} className="inline-flex h-7 items-center gap-1 rounded-md border border-hairline bg-white px-2.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"><RotateCcw className="h-3.5 w-3.5" /> Bring back</button>
          ) : (
            <button type="button" onClick={onOpen} className="inline-flex h-7 items-center rounded-md border border-hairline bg-white px-2.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50">Open</button>
          )}
          {menu.length > 1 && <OverflowMenu items={menu} label="More actions" />}
        </div>
      </div>

      {/* inline editors — never a dropdown */}
      {mode === "snooze" && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2 text-xs">
          <span className="font-semibold text-amber-900">Snooze until</span>
          {[1, 2, 3].map((n) => (
            <button key={n} type="button" onClick={() => setUntil(plusMonths(n))} className={cn("rounded-full px-2 py-0.5 font-medium ring-1 ring-inset", until === plusMonths(n) ? "bg-amber-600 text-white ring-amber-600" : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50")}>
              {MONTH_SHORT[new Date(plusMonths(n) + "T00:00:00").getMonth() + 1]}
            </button>
          ))}
          <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="h-7 rounded-md border border-hairline bg-white px-2 text-xs" />
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why — “customer says November”" className="h-7 min-w-[200px] flex-1 rounded-md border border-hairline bg-white px-2 text-xs" />
          <button type="button" disabled={!until || act.isPending} onClick={() => act.mutate({ id: r.id, action: "snooze", when: until, reason: reason.trim() || null }, { onSuccess: () => { setMode(null); setReason(""); }, onError: onErrToast("Couldn't snooze it") })} className="rounded-md bg-amber-600 px-2.5 py-1 font-semibold text-white hover:bg-amber-700 disabled:opacity-50">Snooze</button>
          <button type="button" onClick={() => setMode(null)} className="text-slate-500 hover:text-slate-700">Cancel</button>
        </div>
      )}
      {mode === "defer" && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2 text-xs">
          <span className="font-semibold text-amber-900">Deferred — why?</span>
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus placeholder="“furnace needs a part — customer deciding”, “A/C not installed yet”" className="h-7 min-w-[260px] flex-1 rounded-md border border-hairline bg-white px-2 text-xs" />
          <span className="text-slate-500">until</span>
          <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="h-7 rounded-md border border-hairline bg-white px-2 text-xs" title="Optional — when to look at it again" />
          <button type="button" disabled={!reason.trim() || act.isPending} onClick={() => act.mutate({ id: r.id, action: "defer", when: until || null, reason: reason.trim() }, { onSuccess: () => { setMode(null); setReason(""); }, onError: onErrToast("Couldn't defer it") })} className="rounded-md bg-amber-600 px-2.5 py-1 font-semibold text-white hover:bg-amber-700 disabled:opacity-50">Defer</button>
          <button type="button" onClick={() => setMode(null)} className="text-slate-500 hover:text-slate-700">Cancel</button>
        </div>
      )}
      {mode === "decline" && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
          <span className="font-semibold text-slate-800">Declined — why?</span>
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus placeholder="“Moved”, “sold the house”, “doesn't want it this year”" className="h-7 min-w-[240px] flex-1 rounded-md border border-hairline bg-white px-2 text-xs" />
          <button type="button" disabled={!reason.trim() || act.isPending} onClick={() => act.mutate({ id: r.id, action: "decline", reason: reason.trim() }, { onSuccess: () => { setMode(null); setReason(""); }, onError: onErrToast("Couldn't record the decline") })} className="rounded-md bg-slate-800 px-2.5 py-1 font-semibold text-white hover:bg-slate-900 disabled:opacity-50">Decline</button>
          <button type="button" onClick={() => setMode(null)} className="text-slate-500 hover:text-slate-700">Cancel</button>
        </div>
      )}
      {mode === "book" && (
        <CreateDispatchModal
          custno={r.custno}
          locno={r.locno}
          customerName={name}
          address={r.address}
          city={r.city}
          phone={r.phone}
          initialDescription={`${r.task_label} — plan visit${r.renewal_at_this_visit ? " · renewal at this visit" : ""}${r.esc_dispatch ? ` · ESC ticket #${r.esc_dispatch}` : ""}${r.status === "picked" ? ` · member picked ${fmtDay(r.booked_date)} ${(r.reason ?? "").replace(/^picked\s*/, "")}` : ""}`}
          prefillDate={r.status === "picked" ? r.booked_date : null}
          prefillWindow={r.status === "picked" ? ((r.reason ?? "").replace(/^picked\s*/, "") || null) : null}
          sourceCaption="From the Season board — until cutover, key the appointment into ESC too; Pegasus reads it back within 15 minutes."
          onCreated={(o) => {
            if (o.mode === "scheduled") act.mutate({ id: r.id, action: "booked", when: o.date, reason: "booked in Pegasus" }, { onError: onErrToast("Booked, but the visit didn't update") });
            else act.mutate({ id: r.id, action: "note", reason: "Pegasus ticket queued — not booked yet" }, { onError: onErrToast("Queued, but the visit didn't update") });
          }}
          onClose={() => setMode(null)}
        />
      )}
    </li>
  );
}
